import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';

import { ECustomFieldType, ICustomField } from 'v2/modules/custom-field/interfaces/custom-field.interface';

interface FilterValue {
  operator: string;
  value: any;
}

interface OperatorOption {
  value: string;
  label: string;
}

@Component({
  selector: 'filter-custom-field',
  templateUrl: './filter-custom-field.component.html',
  styleUrls: ['./filter-custom-field.component.scss'],
})
export class FilterCustomFieldComponent implements OnInit, OnDestroy {
  @Input() customField: ICustomField;
  @Input() value: FilterValue;
  @Output() changeValue = new EventEmitter<FilterValue>();

  #destroy$ = new Subject<void>();
  eCustomFieldType = ECustomFieldType;
  
  availableOperators: OperatorOption[] = [];
  currentOperator = 'eq';
  currentValue: any = '';

  ngOnInit(): void {
    this.initializeOperators();
    this.initializeValue();
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  private initializeOperators(): void {
    if (!this.customField) return;

    switch (this.customField.type) {
      case ECustomFieldType.NUMBER:
        this.availableOperators = [
          { value: 'eq', label: 'Equals' },
          { value: 'neq', label: 'Not Equals' },
          { value: 'gt', label: 'Greater Than' },
          { value: 'gte', label: 'Greater Than or Equal' },
          { value: 'lt', label: 'Less Than' },
          { value: 'lte', label: 'Less Than or Equal' },
          { value: 'in', label: 'In List' },
          { value: 'notin', label: 'Not In List' },
          { value: 'set', label: 'Has Value' },
          { value: 'notset', label: 'Is Empty' },
        ];
        break;
      
      case ECustomFieldType.DATE:
        this.availableOperators = [
          { value: 'eq', label: 'On Date' },
          { value: 'neq', label: 'Not On Date' },
          { value: 'gt', label: 'After' },
          { value: 'gte', label: 'On or After' },
          { value: 'lt', label: 'Before' },
          { value: 'lte', label: 'On or Before' },
          { value: 'set', label: 'Has Value' },
          { value: 'notset', label: 'Is Empty' },
        ];
        break;
      
      case ECustomFieldType.DROPDOWN:
        this.availableOperators = [
          { value: 'eq', label: 'Equals' },
          { value: 'neq', label: 'Not Equals' },
          { value: 'in', label: 'In List' },
          { value: 'notin', label: 'Not In List' },
          { value: 'set', label: 'Has Value' },
          { value: 'notset', label: 'Is Empty' },
        ];
        break;
      
      default: // STRING
        this.availableOperators = [
          { value: 'eq', label: 'Equals' },
          { value: 'neq', label: 'Not Equals' },
          { value: 'contains', label: 'Contains' },
          { value: 'notcontains', label: 'Does Not Contain' },
          { value: 'in', label: 'In List' },
          { value: 'notin', label: 'Not In List' },
          { value: 'set', label: 'Has Value' },
          { value: 'notset', label: 'Is Empty' },
        ];
        break;
    }
  }

  private initializeValue(): void {
    if (this.value) {
      this.currentOperator = this.value.operator || 'eq';
      this.currentValue = this.value.value || this.getInitialValue();
    } else {
      this.currentOperator = this.getDefaultOperator();
      this.currentValue = this.getInitialValue();
    }
  }

  private getDefaultOperator(): string {
    switch (this.customField?.type) {
      case ECustomFieldType.NUMBER:
        return 'gte';
      case ECustomFieldType.DATE:
        return 'gte';
      case ECustomFieldType.STRING:
        return 'contains';
      default:
        return 'eq';
    }
  }

  private getInitialValue(): any {
    if (this.customField?.type === ECustomFieldType.NUMBER) {
      return null;
    }
    if (this.customField?.type === ECustomFieldType.DATE) {
      return null;
    }
    return '';
  }

  onOperatorChange(operator: string): void {
    this.currentOperator = operator;
    
    // Clear value for set/notset operators since they don't need values
    if (operator === 'set' || operator === 'notset') {
      this.currentValue = '1';
    } else if (!this.currentValue) {
      this.currentValue = this.getInitialValue();
    }
    
    this.emitChange();
  }

  onValueChange(value: any): void {
    this.currentValue = value;
    this.emitChange();
  }

  private emitChange(): void {
    const filterValue: FilterValue = {
      operator: this.currentOperator,
      value: this.formatValue(this.currentValue)
    };
    this.changeValue.emit(filterValue);
  }

  private formatValue(value: any): any {
    // Handle array values for 'in' and 'notin' operators
    if ((this.currentOperator === 'in' || this.currentOperator === 'notin') && 
        typeof value === 'string' && value.includes(',')) {
      return value.split(',').map(v => v.trim()).join(',');
    }
    
    // Validate number values
    if (this.customField?.type === ECustomFieldType.NUMBER && 
        this.currentOperator !== 'set' && 
        this.currentOperator !== 'notset') {
      const numValue = parseFloat(value);
      return isNaN(numValue) ? null : numValue;
    }
    
    return value;
  }

  clearValue(): void {
    this.currentOperator = this.getDefaultOperator();
    this.currentValue = this.getInitialValue();
    this.emitChange();
  }

  // Helper method to check if operator needs a value input
  operatorNeedsValue(operator: string): boolean {
    return operator !== 'set' && operator !== 'notset';
  }

  // Helper method to get placeholder text
  getPlaceholder(): string {
    if (this.currentOperator === 'in' || this.currentOperator === 'notin') {
      return `Enter comma-separated values for ${this.customField?.label}`;
    }
    return `Enter ${this.customField?.label}`;
  }
} 